import type { TranslateFn, TranslationKey } from "./i18n/store.js";

/**
 * Canonical genre/tone options offered as chips on the Create page and the
 * editor's Settings tab. The English *value* is what's stored/sent; only the
 * shown label is localized via the label-key maps below. Each option also has a
 * one-line description (the `*_DESC_KEYS` maps) shown as a hint under the chips.
 */
export const CREATE_GENRES = [
  "Mystery",
  "Romance",
  "Fantasy",
  "Sci-Fi",
  "Horror",
  "Thriller",
  "Adventure",
  "Drama",
  "Comedy",
  "Historical"
] as const;

export const CREATE_TONES = [
  "Mysterious",
  "Hopeful",
  "Dark",
  "Heartwarming",
  "Tense",
  "Melancholic",
  "Whimsical",
  "Bittersweet"
] as const;

export type Genre = (typeof CREATE_GENRES)[number];
export type Tone = (typeof CREATE_TONES)[number];

export const GENRE_LABEL_KEYS: Record<Genre, TranslationKey> = {
  Mystery: "create.genre.mystery",
  Romance: "create.genre.romance",
  Fantasy: "create.genre.fantasy",
  "Sci-Fi": "create.genre.sciFi",
  Horror: "create.genre.horror",
  Thriller: "create.genre.thriller",
  Adventure: "create.genre.adventure",
  Drama: "create.genre.drama",
  Comedy: "create.genre.comedy",
  Historical: "create.genre.historical"
};

export const TONE_LABEL_KEYS: Record<Tone, TranslationKey> = {
  Mysterious: "create.tone.mysterious",
  Hopeful: "create.tone.hopeful",
  Dark: "create.tone.dark",
  Heartwarming: "create.tone.heartwarming",
  Tense: "create.tone.tense",
  Melancholic: "create.tone.melancholic",
  Whimsical: "create.tone.whimsical",
  Bittersweet: "create.tone.bittersweet"
};

export const GENRE_DESC_KEYS: Record<Genre, TranslationKey> = {
  Mystery: "create.genreDesc.mystery",
  Romance: "create.genreDesc.romance",
  Fantasy: "create.genreDesc.fantasy",
  "Sci-Fi": "create.genreDesc.sciFi",
  Horror: "create.genreDesc.horror",
  Thriller: "create.genreDesc.thriller",
  Adventure: "create.genreDesc.adventure",
  Drama: "create.genreDesc.drama",
  Comedy: "create.genreDesc.comedy",
  Historical: "create.genreDesc.historical"
};

export const TONE_DESC_KEYS: Record<Tone, TranslationKey> = {
  Mysterious: "create.toneDesc.mysterious",
  Hopeful: "create.toneDesc.hopeful",
  Dark: "create.toneDesc.dark",
  Heartwarming: "create.toneDesc.heartwarming",
  Tense: "create.toneDesc.tense",
  Melancholic: "create.toneDesc.melancholic",
  Whimsical: "create.toneDesc.whimsical",
  Bittersweet: "create.toneDesc.bittersweet"
};

// Case-insensitive lookup from a stored genre value to its label key. Novels
// keep the English canonical genre ("Romance") as their data; only the shown
// label is localized.
// Beyond the canonical create-page genres, the generator freely tags novels
// with other common genre words; these get a translated label too.
const EXTRA_GENRE_TAG_KEYS: Record<string, TranslationKey> = {
  supernatural: "genre.tag.supernatural",
  psychological: "genre.tag.psychological",
  gothic: "genre.tag.gothic",
  school: "genre.tag.school",
  noir: "genre.tag.noir",
  detective: "genre.tag.detective",
  crime: "genre.tag.crime",
  paranormal: "genre.tag.paranormal",
  suspense: "genre.tag.suspense",
  action: "genre.tag.action",
  tragedy: "genre.tag.tragedy",
  dystopian: "genre.tag.dystopian",
  dystopia: "genre.tag.dystopian",
  cyberpunk: "genre.tag.cyberpunk",
  "post-apocalyptic": "genre.tag.post-apocalyptic",
  "slice of life": "genre.tag.slice-of-life",
  "slice-of-life": "genre.tag.slice-of-life",
  survival: "genre.tag.survival",
  "dark fantasy": "genre.tag.dark-fantasy",
  "urban fantasy": "genre.tag.urban-fantasy",
  occult: "genre.tag.occult",
  folklore: "genre.tag.folklore",
  "science fiction": "create.genre.sciFi",
  scifi: "create.genre.sciFi",
  romantic: "create.genre.romance"
};

const GENRE_LABEL_KEY_BY_VALUE: Map<string, TranslationKey> = new Map([
  ...(Object.entries(GENRE_LABEL_KEYS) as [Genre, TranslationKey][]).map(
    ([value, key]) => [value.toLowerCase(), key] as const
  ),
  ...Object.entries(EXTRA_GENRE_TAG_KEYS)
]);

/**
 * Localized label for a genre tag as displayed on cards and detail/author
 * pages. Canonical genres are translated via their label key; any other tag
 * (e.g. a custom, LLM-authored genre word) is shown verbatim.
 */
export function localizeGenre(tag: string, t: TranslateFn): string {
  const key = GENRE_LABEL_KEY_BY_VALUE.get(tag.trim().toLowerCase());
  return key ? t(key) : tag;
}
